import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};
import posthog from 'posthog-js';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import AccountDeletionNotice from '@/components/AccountDeletionNotice';
import { saveSession, getSession, clearSession } from '@/utils/sessionStore';
import { registerNativePush } from '@/utils/nativePush';
import { forgetThisBrowsersPush } from '@/hooks/usePushNotifications';
import { IS_NATIVE } from '@/lib/nativeContext';
import { registerNativeDeepLinks } from '@/utils/nativeDeepLinks';
import { setNativeBadge, clearNativeBadge, onNativeResume } from '@/utils/nativeBadge';
import { authHeaders } from '@/utils/authHeaders';

// Set up axios to send Bearer token from localStorage on every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('sessionToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 (expired/invalid session)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('sessionToken')) {
      // Iter 118.15 — nuke user-scoped Journal caches on session
      // expiry too, not just the explicit logout button.
      import('./pages/journal/journalCache')
        .then((m) => m.clearAllCaches())
        .catch(() => {});
      clearSession();
      window.location.href = '/onboarding?login=true';
    }
    return Promise.reject(error);
  }
);

// Pages
// LandingPage and AuthCallback stay eager: LandingPage is the entry route
// rendered before any Suspense boundary is useful, and AuthCallback is
// returned from AppRoutes outside the Suspense-wrapped routes tree (see
// the `session_id=` early return below), so a lazy chunk there would
// suspend with no boundary above it.
import LandingPage from '@/pages/LandingPage';
import AuthCallback from '@/pages/AuthCallback';

// Everything else is route-code-split: each page ships in its own chunk,
// fetched only when that route is visited, instead of all ~45 pages
// (including admin/super-admin/enterprise consoles and the mapbox-gl-heavy
// PrayerIsRisingPage) riding along in the single main bundle.
const OnboardingPage = React.lazy(() => import('@/pages/OnboardingPage'));
const ConfirmationPage = React.lazy(() => import('@/pages/ConfirmationPage'));
const RequestorDashboard = React.lazy(() => import('@/pages/RequestorDashboard'));
const SubmitRequestPage = React.lazy(() => import('@/pages/SubmitRequestPage'));
const RequestDetailPage = React.lazy(() => import('@/pages/RequestDetailPage'));
const AdminDashboard = React.lazy(() => import('@/pages/AdminDashboard'));
const MessagesPage = React.lazy(() => import('@/pages/MessagesPage'));
const ProfilePage = React.lazy(() => import('@/pages/ProfilePage'));
const PrayerIsRisingPage = React.lazy(() => import('@/pages/PrayerIsRisingPage'));
const WarriorApplicationPage = React.lazy(() => import('@/pages/WarriorApplicationPage'));
const WarriorPendingPage = React.lazy(() => import('@/pages/WarriorPendingPage'));
const AdminPrayerOpsConsole = React.lazy(() => import('@/pages/AdminPrayerOpsConsole'));
const CommunitiesHub = React.lazy(() => import('@/pages/CommunitiesHub'));
const CommunityDetailPage = React.lazy(() => import('@/pages/CommunityDetailPage'));
const StartGroupPage = React.lazy(() => import('@/pages/StartGroupPage'));
const BillingSuccessPage = React.lazy(() => import('@/pages/BillingSuccessPage'));
const BillingCancelPage = React.lazy(() => import('@/pages/BillingCancelPage'));
// Family & Friends plan: pricing, "My plan", and the two pages Stripe Checkout returns to.
const PricingPage = React.lazy(() => import('@/pages/PricingPage'));
const MyPlanPage = React.lazy(() => import('@/pages/MyPlanPage'));
const PlanSuccessPage = React.lazy(() => import('@/pages/PlanSuccessPage'));
const PlanCancelPage = React.lazy(() => import('@/pages/PlanCancelPage'));
// Public — opened from an email by someone who may have no account at all.
const BillingDelegatePage = React.lazy(() => import('@/pages/BillingDelegatePage'));
const CommunityAdminConsole = React.lazy(() => import('@/pages/CommunityAdminConsole'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const PaymentSuccessPage = React.lazy(() => import('@/pages/PaymentSuccessPage'));
const PrayTodayPage = React.lazy(() => import('@/pages/PrayTodayPage'));
const ResourcePage = React.lazy(() => import('@/pages/ResourcePage'));
const InviteLandingPage = React.lazy(() => import('@/pages/InviteLandingPage'));
const ClaimLandingPage = React.lazy(() => import('@/pages/ClaimLandingPage'));
const AdminClaimLandingPage = React.lazy(() => import('@/pages/AdminClaimLandingPage'));
const SubmitForSomeoneElsePage = React.lazy(() => import('@/pages/SubmitForSomeoneElsePage'));
const TermsPage = React.lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('@/pages/PrivacyPage'));
const CommunityStandardsPage = React.lazy(() => import('@/pages/CommunityStandardsPage'));
const CommunityGuidelinesPage = React.lazy(() => import('@/pages/CommunityGuidelinesPage'));
const SafetyContactPage = React.lazy(() => import('@/pages/SafetyContactPage'));
const ContactPage = React.lazy(() => import('@/pages/ContactPage'));
const CookiePolicyPage = React.lazy(() => import('@/pages/CookiePolicyPage'));
const SmsOptInProofPage = React.lazy(() => import('@/pages/SmsOptInProofPage'));
const SuperAdminPage = React.lazy(() => import('@/pages/SuperAdminPage'));
const EnterpriseSetupPage = React.lazy(() => import('@/pages/super_admin/EnterpriseSetupPage'));
const SponsorCommunitiesPage = React.lazy(() => import('@/pages/super_admin/SponsorCommunitiesPage'));
const BulkCommunitySetupPage = React.lazy(() => import('@/pages/super_admin/BulkCommunitySetupPage'));
const OrganizationSponsorshipsPage = React.lazy(() => import('@/pages/super_admin/OrganizationSponsorshipsPage'));
const PendingAdminInvitesPage = React.lazy(() => import('@/pages/super_admin/PendingAdminInvitesPage'));
// Iter 118.1 — Auth-aware link resolver
const GoRedirect = React.lazy(() => import('@/pages/GoRedirect'));
const AccessDeniedPage = React.lazy(() => import('@/pages/AccessDeniedPage'));
const TestimonyPage = React.lazy(() => import('@/pages/TestimonyPage'));

import BottomNav from '@/components/BottomNav';
import GetTheAppBar from '@/components/GetTheAppBar';
import TopNav from '@/components/TopNav';
import CompleteProfileGate from '@/components/CompleteProfileGate';
import { captureAndBuildLoginUrl, consumePendingRedirect, isSafeRelativePath, markVoluntaryLogout, clearPendingRedirect } from '@/lib/auth-redirect';

// Shared fallback shown while a lazy route chunk downloads. Mirrors the
// App-level auth-check loading screen so route transitions don't flash an
// unstyled blank page.
const RouteLoadingFallback = () => (
  <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
    <div className="text-[#8C9696]">Loading...</div>
  </div>
);

// Iter 118.1 — Module-scoped so React doesn't remount the whole
// subtree on every AppRoutes render (rules-of-hooks + performance).
// Reads its own location so it doesn't need a closure over the parent.
function RequireAuthRedirect() {
  const loc = useLocation();
  const loginUrl = captureAndBuildLoginUrl(loc);
  return <Navigate to={loginUrl} replace />;
}

function App() {
  // Read localStorage synchronously for initial state to prevent redirect race condition
  const getInitialUser = () => {
    try {
      const storedUser = localStorage.getItem('mustardSeedlingsUser');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      return null;
    }
  };

  const [currentUser, setCurrentUser] = useState(getInitialUser);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  // Iter 118.27 — surfaced from /auth/me so the app can tell the user a
  // pending account deletion was undone by signing in, instead of reversing it
  // silently.
  const [deletionState, setDeletionState] = useState({ cancelledOnLogin: false, scheduledDeletionAt: null });
  // App Review Guideline 1.2 — /auth/me returns 403 {code: "account_suspended"}
  // for a super-admin-suspended account. Surfaced here (rather than a toast,
  // which the app's own Toaster may not be mounted yet this early in
  // session bootstrap) so the login screen can show something honest
  // instead of just looking like an ordinary logged-out state.
  const [suspendedReason, setSuspendedReason] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Iter 118.10a — silent timezone resync. PATCHes only when the
  // browser's current tz differs from the tz stored on the user
  // record. Called from both handleUserLogin (fresh login) and
  // restoreSession (session bootstrap), so a user who moved / travelled
  // since last login gets their reminders + Daily Moment retimed
  // without noise.
  const maybeResyncTimezone = useCallback((user, token) => {
    if (!user?.id) return;
    let browserTz;
    try { browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch (_) { return; }
    if (!browserTz) return;
    if (user.timezone === browserTz) return;  // fast path — nothing to do
    fetch(`${API}/users/${user.id}/timezone`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ timezone: browserTz }),
    }).then(async (r) => {
      if (r.ok) {
        // Mirror onto the cached user object so the next call skips
        // the PATCH.
        const updated = { ...user, timezone: browserTz };
        localStorage.setItem('mustardSeedlingsUser', JSON.stringify(updated));
      }
    }).catch(() => {});
  }, [API]);

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/notifications/${userId}/unread-count`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('sessionToken') || ''}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
        // The store app's icon first (no-op on the web)...
        setNativeBadge(data.count || 0);
        // ...then the installed web app's badge. The native web view also
        // exposes setAppBadge but throws when it is called, so this stays
        // isolated: it must never take the native badge down with it.
        try {
          if ('setAppBadge' in navigator) {
            if (data.count > 0) navigator.setAppBadge(data.count).catch(() => {});
            else navigator.clearAppBadge().catch(() => {});
          }
        } catch (_) { /* not an installed web app */ }
      }
    } catch (e) { /* silent */ }
  }, [API]);

  // Iter 118.36 — a user who's already logged in and clicks one invite
  // link only joins that one community (InviteLandingPage's own "Join
  // Community" button just calls POST /memberships for that one id). Any
  // OTHER pending invites for their email sit there until they separately
  // click through each of those emails too. Running this on every session
  // restore means having an account is enough on its own — claim_pending_
  // community_invites matches purely by email and is a no-op once nothing
  // is left pending, so this is cheap and safe to call on every app open.
  const claimPendingInvites = useCallback(async (userId) => {
    if (!userId) return;
    try {
      await fetch(`${API}/communities/claim-invites?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
        // The server takes the person from their login, not from user_id. A plain
        // fetch does not get the token from the axios interceptor, and the app
        // shells that run from another origin send no cookie.
        headers: authHeaders(),
      });
    } catch (e) { /* silent */ }
  }, [API]);

  useEffect(() => {
    let cancelled = false;
    const restoreSession = async () => {
      // Read from localStorage + IndexedDB fallback
      const { token: storedToken, user: storedUser } = await getSession();

      // If we have a stored user, show it immediately to avoid flash
      if (storedUser) setCurrentUser(storedUser);

      // ALWAYS validate with the backend regardless of stored state
      if (storedToken) {
        try {
          const res = await fetch(`${API}/auth/me`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          if (cancelled) return;
          if (res.ok) {
            const data = await res.json();
            if (data.user) {
              await saveSession(data.session_token || storedToken, data.user);
              setCurrentUser(data.user);
              setDeletionState({
                cancelledOnLogin: !!data.deletionCancelledOnLogin,
                scheduledDeletionAt: data.scheduledDeletionAt || null,
              });
              fetchUnreadCount(data.user.id);
              claimPendingInvites(data.user.id);
              // Iter 118.10a — resync timezone on session bootstrap
              // (device may have moved / travelled since last login).
              // Only PATCHes when the browser tz differs from stored,
              // so this is silent + free for the common case.
              maybeResyncTimezone(data.user, data.session_token || storedToken);
              setIsLoading(false);
              return;
            }
          }
          // Token invalid — clear all stored data
          if (res.status === 403) {
            try {
              const errData = await res.json();
              if (errData?.detail?.code === 'account_suspended') {
                setSuspendedReason(errData.detail.reason || true);
              }
            } catch (_) { /* not JSON — fall through as an ordinary invalid session */ }
          }
          await clearSession();
          setCurrentUser(null);
        } catch (e) {
          if (cancelled) return;
          // Network error — trust stored data (offline PWA)
          if (storedUser?.id) {
            fetchUnreadCount(storedUser.id);
            setIsLoading(false);
            return;
          }
        }
      }

      // No token found — try cookie-only session restore (last fallback)
      if (!storedToken) {
        try {
          const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
          if (cancelled) return;
          if (res.ok) {
            const data = await res.json();
            if (data.user && data.session_token) {
              await saveSession(data.session_token, data.user);
              setCurrentUser(data.user);
              setDeletionState({
                cancelledOnLogin: !!data.deletionCancelledOnLogin,
                scheduledDeletionAt: data.scheduledDeletionAt || null,
              });
              fetchUnreadCount(data.user.id);
              claimPendingInvites(data.user.id);
              maybeResyncTimezone(data.user, data.session_token);
              setIsLoading(false);
              return;
            }
          } else if (res.status === 403) {
            try {
              const errData = await res.json();
              if (errData?.detail?.code === 'account_suspended') {
                setSuspendedReason(errData.detail.reason || true);
              }
            } catch (_) { /* not JSON */ }
          }
        } catch (e) { /* no valid cookie session */ }
        setCurrentUser(null);
      }

      setIsLoading(false);
    };
    restoreSession();

    // Clear home screen badge when app is opened (installed web app only;
    // the store app sets its own from the unread count instead)
    try {
      if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
    } catch (_) { /* native web view: clearAppBadge exists but throws */ }
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage('CLEAR_BADGE');
    }

    return () => { cancelled = true; };
  }, [API, fetchUnreadCount, maybeResyncTimezone, claimPendingInvites]);

  // Poll unread count every 60 seconds
  useEffect(() => {
    if (!currentUser?.id) return;
    const interval = setInterval(() => fetchUnreadCount(currentUser.id), 60000);
    return () => clearInterval(interval);
  }, [currentUser?.id, fetchUnreadCount]);

  // Native push (Android/iOS via FCM) — no-ops on web, see nativePush.js.
  useEffect(() => {
    if (!currentUser?.id) return;
    registerNativePush(currentUser.id);
  }, [currentUser?.id]);

  // Store app coming back to the foreground: refresh the count so the icon
  // badge drops when alerts were read elsewhere (web, another device).
  useEffect(() => {
    if (!currentUser?.id) return undefined;
    return onNativeResume(() => fetchUnreadCount(currentUser.id));
  }, [currentUser?.id, fetchUnreadCount]);

  const handleUserLogin = useCallback((user, sessionToken) => {
    setCurrentUser(user);
    // Save to localStorage + IndexedDB for resilient PWA persistence
    const token = sessionToken || localStorage.getItem('sessionToken');
    if (token) saveSession(token, user);
    else localStorage.setItem('mustardSeedlingsUser', JSON.stringify(user));
    // This device has signed in at least once. Deliberately survives logout:
    // the landing page uses it to put "Log In" first for returning members
    // and "Bring your first prayer" first for a brand-new device.
    try { localStorage.setItem('gr8ful.knownDevice', '1'); } catch (_) { /* storage blocked */ }
    // The id only: no e-mail or name goes to PostHog.
    posthog.identify(user.id);
    posthog.capture('user_logged_in');
    // Iter 118.10 → 118.10a — reuse the shared resync helper.
    maybeResyncTimezone(user, token);
    fetchUnreadCount(user.id);

    // NOTE: pending on-behalf claim acceptance is now finalized inside
    // OnboardingPage directly (both login + signup flows) so the user lands
    // on /requests/{id} without an interstitial bounce through /claim/{token}.
  }, [fetchUnreadCount]);

  const handleLogout = async () => {
    // Iter 118.36 — logging out from whatever page the Log Out button
    // lives on (almost always Profile) used to send the NEXT login
    // straight back there: RequireAuthRedirect can't tell "I chose to
    // leave" apart from "I got bounced off a deep link" -- both are
    // just "currentUser is null on a protected route" -- so it captured
    // Profile as the return target every time. Flag this as voluntary
    // so the very next redirect skips that capture, and drop any
    // leftover pendingRedirect from an earlier, unrelated bounce too.
    markVoluntaryLogout();
    clearPendingRedirect();
    // Iter 118.15 — clear the user-scoped Journal cache BEFORE we
    // null the session. Belt-and-suspenders via clearAllCaches so
    // shared-device scenarios don't leak the previous user's
    // request titles into the next login.
    try {
      const mod = await import('./pages/journal/journalCache');
      mod.clearAllCaches();
    } catch (_) { /* non-fatal */ }
    // This browser stops getting the account's notifications (P0212). Phones need nothing here: the server removes the
    // registration of the sign-in that logs out.
    if (!IS_NATIVE) await forgetThisBrowsersPush();
    try {
      const token = localStorage.getItem('sessionToken');
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error('Logout API error:', e);
    }
    
    posthog.reset();
    setCurrentUser(null);
    setUnreadCount(0);
    await clearSession();
    clearNativeBadge();
    try {
      if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
    } catch (_) { /* native web view: clearAppBadge exists but throws */ }
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-[#8C9696]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        {/* Android phones' browsers: a bar to get the Play Store app (P0211). iPhone gets Apple's own from index.html. */}
        <GetTheAppBar />
        <AppRoutes 
          currentUser={currentUser} 
          handleUserLogin={handleUserLogin} 
          handleLogout={handleLogout}
          unreadCount={unreadCount}
          fetchUnreadCount={fetchUnreadCount}
          deletionState={deletionState}
          suspendedReason={suspendedReason}
        />
        {/* A toast stays 8 seconds (Sonner's own is 4, barely time to read a sentence) and can be closed; hovering over it holds it. */}
        <Toaster position="top-center" richColors closeButton duration={8000} />
      </BrowserRouter>
    </div>
  );
}

// Separate component to handle routing with location access
function AppRoutes({ currentUser, handleUserLogin, handleLogout, unreadCount, fetchUnreadCount, deletionState, suspendedReason }) {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    registerNativeDeepLinks(navigate);
  }, [navigate]);

  // Iter 118.20.c — Toggle .no-bottom-nav on .App wrapper for routes
  // where BottomNav is hidden (`/`, `/onboarding`, `/auth`). This drops
  // the 72px mobile padding-bottom reservation on those pages so the
  // onboarding cards fit within a 390×844 viewport without scroll.
  // Must mirror BottomNav.jsx's `hideOnPaths` list.
  React.useEffect(() => {
    const hideOnPaths = ['/', '/onboarding', '/auth'];
    const root = document.querySelector('.App');
    if (!root) return;
    if (hideOnPaths.includes(location.pathname)) {
      root.classList.add('no-bottom-nav');
    } else {
      root.classList.remove('no-bottom-nav');
    }
  }, [location.pathname]);

  // Iter 118.1 — Post-login redirect resolver.
  // MUST run BEFORE the session_id early-return below so this hook is
  // called on every render (rules-of-hooks). When we have a currentUser
  // AND the URL carries ?next= OR a pendingRedirect is stored, honor
  // it exactly once and clear it. Only triggers on landing/onboarding
  // routes so we don't yank the user off an already-rendered protected
  // route.
  React.useEffect(() => {
    if (!currentUser) return;
    if (location.pathname !== '/' && location.pathname !== '/onboarding') return;
    const searchParams = new URLSearchParams(location.search);
    const target = consumePendingRedirect(searchParams);
    if (target && isSafeRelativePath(target)) {
      window.location.replace(target);
    }
  }, [currentUser?.id, location.pathname, location.search]);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onUserLogin={handleUserLogin} />;
  }

  // Iter 117.60 — wrap auth routes in CompleteProfileGate so any user
  // with empty city/state (legacy Google OAuth users from before iter
  // 117.60's backend hardening) gets a one-screen city/state capture
  // before they can use the app. The gate's own allowlist permits
  // legal pages and the OAuth callback so users can still log out.
  const routesNode = (
    <Routes>
      <Route path="/" element={currentUser ? <Navigate to="/requestor" replace /> : <LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      {/* /community-standards → /community-guidelines redirect alias.
          Old bookmarks and external links keep working. */}
      <Route path="/community-standards" element={<Navigate to="/community-guidelines" replace />} />
      <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/safety-contact" element={<SafetyContactPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/sms-opt-in" element={<SmsOptInProofPage />} />
      {/* Iter 118.1 — Auth-aware link resolver + access-denied screens */}
      <Route path="/go" element={<GoRedirect user={currentUser} />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/testimonies/:id" element={<TestimonyPage user={currentUser} />} />
      <Route path="/invite/:communityId" element={<InviteLandingPage user={currentUser} />} />
      <Route path="/claim/:token" element={<ClaimLandingPage user={currentUser} onLogout={handleLogout} />} />
      <Route path="/admin-claim/:token" element={<AdminClaimLandingPage user={currentUser} onLogout={handleLogout} />} />
      <Route path="/onboarding" element={<OnboardingPage onUserLogin={handleUserLogin} suspendedReason={suspendedReason} />} />
      <Route path="/billing/success" element={<BillingSuccessPage />} />
      <Route path="/billing/cancel" element={<BillingCancelPage />} />
      {/* Family & Friends plan. /pricing is open to everyone (signed-out visitors
          are sent to sign in when they choose the plan); My plan needs a login; the
          checkout return page thanks a visitor who is not signed in (P0227: in the
          iPhone app Stripe's return opens in a browser window where nobody is); the
          cancel page only says nothing was charged.
          Full URLs open the app through universal links, so each path here is also
          what a link from outside lands on. */}
      <Route path="/pricing" element={<PricingPage user={currentUser} />} />
      <Route path="/plan" element={currentUser ? <MyPlanPage user={currentUser} /> : <RequireAuthRedirect />} />
      <Route path="/plan/success" element={currentUser ? <PlanSuccessPage /> : <PlanSuccessPage signedOut />} />
      <Route path="/plan/cancel" element={<PlanCancelPage />} />
      {/* Deliberately OUTSIDE any auth wrapper: the delegated payer is often
          not a Gr8ful user, and requiring an account here would reintroduce the
          exact friction billing delegation exists to remove. The token in the
          URL is the authorisation. */}
      <Route path="/billing/delegate/:token" element={<BillingDelegatePage />} />
      <Route path="/confirmation" element={<ConfirmationPage user={currentUser} />} />
      <Route 
        path="/requestor" 
        element={currentUser ? <RequestorDashboard user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/requestor/submit" 
        element={currentUser ? <SubmitRequestPage user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/communities/:communityId/submit-for-someone-else" 
        element={currentUser ? <SubmitForSomeoneElsePage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/requestor/request/:id" 
        element={currentUser ? <RequestDetailPage user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/warrior" 
        element={currentUser ? <Navigate to="/pray-today" replace /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/admin" 
        element={currentUser ? <AdminDashboard user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/messages/:requestId" 
        element={currentUser ? <MessagesPage user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/profile" 
        element={currentUser ? <ProfilePage user={currentUser} onLogout={handleLogout} onUserUpdate={handleUserLogin} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/prayer-rising" 
        element={currentUser ? <PrayerIsRisingPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/warrior/apply" 
        element={currentUser ? <WarriorApplicationPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/communities/:communityId/warrior-apply" 
        element={currentUser ? <WarriorApplicationPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/warrior-pending" 
        element={currentUser ? <WarriorPendingPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/admin/prayer-ops" 
        element={currentUser ? <AdminPrayerOpsConsole user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/communities" 
        element={currentUser ? <CommunitiesHub user={currentUser} /> : <RequireAuthRedirect />} 
      />
      {/* The address the old five-step wizard had (links and installed apps point here).
          It now opens the one-screen create: pick who you're praying with, a name, create,
          then bring in the first people. */}
      <Route 
        path="/communities/create" 
        element={currentUser ? <StartGroupPage user={currentUser} /> : <RequireAuthRedirect />}
      />
      <Route 
        path="/communities/:communityId" 
        element={currentUser ? <CommunityDetailPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/communities/:communityId/admin" 
        element={currentUser ? <CommunityAdminConsole user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/notifications" 
        element={currentUser ? <NotificationsPage user={currentUser} onNotificationsRead={() => fetchUnreadCount(currentUser.id)} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/payment/success" 
        element={currentUser ? <PaymentSuccessPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/pray-today" 
        element={currentUser ? <PrayTodayPage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/resources/:resourceId" 
        element={currentUser ? <ResourcePage user={currentUser} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/requests/:id" 
        element={currentUser ? <RequestDetailPage user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/request/:id" 
        element={currentUser ? <RequestDetailPage user={currentUser} onLogout={handleLogout} /> : <RequireAuthRedirect />} 
      />
      <Route 
        path="/super-admin" 
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <SuperAdminPage user={currentUser} />
            : <RequireAuthRedirect />
        } 
      />
      <Route
        path="/super-admin/enterprise"
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <EnterpriseSetupPage user={currentUser} />
            : <RequireAuthRedirect />
        }
      />
      <Route
        path="/super-admin/enterprise/link/:communityId"
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <EnterpriseSetupPage user={currentUser} mode="link" />
            : <RequireAuthRedirect />
        }
      />
      <Route
        path="/super-admin/sponsorships"
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <SponsorCommunitiesPage user={currentUser} />
            : <RequireAuthRedirect />
        }
      />
      <Route
        path="/super-admin/sponsors"
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <OrganizationSponsorshipsPage user={currentUser} />
            : <RequireAuthRedirect />
        }
      />
      <Route
        path="/super-admin/bulk-setup"
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <BulkCommunitySetupPage user={currentUser} />
            : <RequireAuthRedirect />
        }
      />
      <Route
        path="/super-admin/admin-invites"
        element={
          currentUser && currentUser.email === 'prayer@themustardseedlings.com'
            ? <PendingAdminInvitesPage user={currentUser} />
            : <RequireAuthRedirect />
        }
      />
    </Routes>
  );

  return (
    <>
      <TopNav user={currentUser} onLogout={handleLogout} unreadCount={unreadCount} />
      {currentUser && (
        <AccountDeletionNotice
          cancelledOnLogin={deletionState.cancelledOnLogin}
          scheduledDeletionAt={deletionState.scheduledDeletionAt}
          onNavigateToAccount={() => navigate('/profile?sheet=account')}
        />
      )}
      <React.Suspense fallback={<RouteLoadingFallback />}>
        {currentUser ? (
          <CompleteProfileGate
            user={currentUser}
            onUserUpdate={handleUserLogin}
          >
            {routesNode}
          </CompleteProfileGate>
        ) : routesNode}
      </React.Suspense>
      <BottomNav user={currentUser} unreadCount={unreadCount} />
    </>
  );
}

export default App;
